'use client'

import { motion } from 'framer-motion'

interface HomeValuesSectionProps {
  dictionary: Dictionary['home']['values'] // More specific type
  values: {
    icon: React.ReactNode
    title: string
    description: string
  }[]
}

export const HomeValuesSection: React.FC<HomeValuesSectionProps> = ({ dictionary, values }) => {
  return (
    <section className='relative py-16 text-white overflow-hidden'>
      {/* Animated Background */}
      <div className='absolute inset-0 bg-linear-to-r from-blue-900 to-slate-900 opacity-70'>
        <div className='absolute inset-0 opacity-20'>
          <svg
            className='h-full w-full'
            xmlns='http://www.w3.org/2000/svg'
          >
            <defs>
              <pattern
                id='smallGrid'
                width='20'
                height='20'
                patternUnits='userSpaceOnUse'
              >
                <path
                  d='M 20 0 L 0 0 0 20'
                  fill='none'
                  stroke='white'
                  strokeWidth='0.5'
                />
              </pattern>
              <pattern
                id='grid'
                width='80'
                height='80'
                patternUnits='userSpaceOnUse'
              >
                <rect
                  width='80'
                  height='80'
                  fill='url(#smallGrid)'
                />
                <path
                  d='M 80 0 L 0 0 0 80'
                  fill='none'
                  stroke='white'
                  strokeWidth='1'
                />
              </pattern>
            </defs>
            <rect
              width='100%'
              height='100%'
              fill='url(#grid)'
            />
          </svg>
        </div>
        <div className='absolute inset-0'>
          <motion.div
            className='absolute -inset-1/2 opacity-30'
            initial={{ rotate: 0, scale: 1 }}
            animate={{
              rotate: 360,
              scale: [1, 1.1, 1],
              y: [0, -10, 0],
            }}
            transition={{
              duration: 20,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'loop',
              ease: 'linear',
            }}
          >
            <div className='w-full h-full bg-blue-900 rounded-full blur-3xl' />
          </motion.div>
          <motion.div
            className='absolute top-1/4 -right-1/4 w-1/2 h-1/2 opacity-20'
            initial={{ rotate: 0 }}
            animate={{
              rotate: -360,
              x: [0, -20, 0],
              y: [0, 20, 0],
            }}
            transition={{
              duration: 25,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'loop',
              ease: 'linear',
            }}
          >
            <div className='w-full h-full bg-black rounded-full blur-3xl' />
          </motion.div>
          <motion.div
            className='absolute -bottom-1/4 left-1/4 w-1/2 h-1/2 opacity-20'
            initial={{ rotate: 0 }}
            animate={{
              rotate: 360,
              x: [0, 20, 0],
              y: [0, -20, 0],
            }}
            transition={{
              duration: 30,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'loop',
              ease: 'linear',
            }}
          >
            <div className='w-full h-full bg-blue-700 rounded-full blur-3xl' />
          </motion.div>
        </div>
        <motion.div
          className='absolute inset-0'
          initial={{ opacity: 0.1 }}
          animate={{ opacity: [0.1, 0.15, 0.1] }}
          transition={{
            duration: 5,
            repeat: Number.POSITIVE_INFINITY,
            repeatType: 'reverse',
          }}
        >
          {Array.from({ length: 20 }).map((_, i) => {
            // Use predetermined positions based on index
            const top = (i * 17) % 100
            const left = (i * 23) % 100
            const size = (i % 3) + 2
            return (
              <motion.div
                key={i}
                className='absolute rounded-full bg-white'
                style={{
                  top: `${top}%`,
                  left: `${left}%`,
                  width: `${size}px`,
                  height: `${size}px`,
                }}
                initial={{ opacity: 0.1, y: 0 }}
                animate={{
                  opacity: [0.1, 0.8, 0.1],
                  y: -100,
                }}
                transition={{
                  duration: 10 + i * 0.5,
                  repeat: Number.POSITIVE_INFINITY,
                  delay: i * 0.25,
                }}
              />
            )
          })}
        </motion.div>
      </div>

      {/* Content */}
      <div className='container mx-auto relative z-10'>
        <motion.div
          className='mb-12 text-center'
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className='text-sm font-medium tracking-wider text-blue-100'>{dictionary.subtitle}</span>
          <h2 className='mt-2 text-3xl font-bold tracking-tight text-slate-100 md:text-4xl'>{dictionary.title}</h2>
        </motion.div>

        <div className='grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4'>
          {values.map((value, index) => (
            <motion.div
              key={index}
              className='rounded-lg bg-black/30 p-6 shadow-md transition-all duration-300 hover:-translate-y-1 border hover:shadow-lg  backdrop-blur-xs border-white/10'
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
            >
              <div className='mb-4 rounded-lg bg-blue-100 p-3 inline-flex'>{value.icon}</div>
              <h3 className='mb-2 text-xl font-bold text-slate-200'>{value.title}</h3>
              <p className='text-slate-300'>{value.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
